import React, { useEffect, useRef, useState } from 'react';
import {
  RegisterConfirmItem,
  RegisterLeadCandidate,
  answerRegisterAction,
  cancelRegisterAction,
  confirmRegisterAction,
  undoRegisterAction,
} from '../services/MendsVisionService';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

/**
 * Controles de um item do Registrar que dependem do usuário:
 * - mensagem/ação externa preparada: mostra exatamente o que sai, para quem (nome + final do telefone), por qual canal
 *   e quando, com edição do texto, e espera UM clique (a única confirmação do Registrar);
 * - alvo ambíguo: lista os candidatos com o que distingue cada um (final do telefone, etapa, última conversa);
 * - comando que precisa de esclarecimento: campo "Sua resposta" (com ou sem candidatos) que o servidor reinterpreta;
 * - comando executado com desfazer.
 */
export interface RegisterActionControlsProps {
  sectorId: number;
  requestId: string;
  item: RegisterConfirmItem;
  onUpdate: (item: RegisterConfirmItem) => void;
}

function when(iso?: string | null) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

export function CandidateLine({ c }: { c: RegisterLeadCandidate }) {
  const bits = [
    c.phoneTail ? `final ${c.phoneTail}` : null,
    c.column ? `etapa: ${c.column}` : null,
    c.lastMessageAt ? `última conversa ${when(c.lastMessageAt)}` : null,
  ].filter(Boolean);
  return (
    <span>
      <strong>{c.name}</strong>
      {bits.length > 0 && <small> · {bits.join(' · ')}</small>}
      {c.lastMessage && <small className="mai-reg-candidate-last">“{c.lastMessage}”</small>}
    </span>
  );
}

/** Resposta curta ao esclarecimento: Enter envia; vazio ou ocupado desabilita. Resposta processada limpa o campo. */
function AnswerField({
  item, busy, onSend,
}: { item: RegisterConfirmItem; busy: boolean; onSend: (answer: string) => Promise<boolean> }) {
  const [answer, setAnswer] = useState('');
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const id = `mai-reg-answer-${item.actionId}`;
  const send = async () => {
    if (busy || !answer.trim()) return;
    if (await onSend(answer.trim()) && mounted.current) setAnswer('');
  };
  return (
    <div className="mai-reg-answer" role="group" aria-label="Responder o esclarecimento">
      <label className="mai-approval-label" htmlFor={id}>Sua resposta</label>
      <div className="mai-btn-row">
        <input
          id={id}
          className="mai-approval-edit mai-approval-edit-line"
          type="text"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void send();
            }
          }}
          maxLength={500}
          disabled={busy}
        />
        <button type="button" className="mai-btn" disabled={busy || !answer.trim()} onClick={() => void send()}>
          {busy ? 'Enviando…' : 'Responder'}
        </button>
      </div>
    </div>
  );
}

export const RegisterActionControls: React.FC<RegisterActionControlsProps> = ({ sectorId, requestId, item, onUpdate }) => {
  const outcome = (item.outcome || '').toLowerCase();
  const preview = item.preview;
  const [text, setText] = useState(preview?.text || '');
  const [subject, setSubject] = useState(preview?.subject || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A prévia pode chegar depois da montagem (resposta a um esclarecimento vira "aguardando confirmação"): reaplica só quando
  // o texto/assunto do servidor muda, sem atropelar a edição do usuário no mesmo preview.
  useEffect(() => { setText(preview?.text || ''); }, [item.actionId, preview?.text]);
  useEffect(() => { setSubject(preview?.subject || ''); }, [item.actionId, preview?.subject]);

  // `sendsEmail`: o clique que envia o e-mail. Só nele, erro de rede/timeout ou 5xx deixa o envio incerto (pode ter saído);
  // 400/409 e a falha de Cancelar mostram a mensagem real do servidor.
  const run = async (fn: () => Promise<RegisterConfirmItem>, sendsEmail = false): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      onUpdate(await fn());
      return true;
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } } | null)?.response?.status;
      const uncertain = sendsEmail && (status === undefined || status >= 500);
      setError(uncertain
        ? 'Não sei se o e-mail saiu. Confira em Conversas antes de tentar de novo (clicar de novo não envia duas vezes).'
        : apiErrorMessage(e, 'Não consegui concluir. Nada foi enviado.'));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const base = { sectorId, requestId, actionId: item.actionId };

  // E-mail avulso: para quem, de qual caixa, assunto e texto editáveis; o único clique é "Enviar e-mail".
  if (outcome === 'awaiting_confirmation' && preview?.kind === 'email') {
    return (
      <div className="mai-reg-confirm" role="group" aria-label="Confirmar envio do e-mail">
        <p className="mai-reg-confirm-to">
          Para <strong>{preview.contactName}</strong> &lt;{preview.emailTo}&gt; · E-mail · {preview.whenLabel || 'agora'}
        </p>
        <p className="mai-reg-confirm-to">De: {preview.emailFrom}</p>
        <label className="mai-approval-label" htmlFor={`mai-reg-subject-${item.actionId}`}>
          {preview.subjectDrafted ? 'Assunto sugerido pela IA (pode editar)' : 'Assunto (pode editar)'}
        </label>
        <input
          id={`mai-reg-subject-${item.actionId}`}
          className="mai-approval-edit mai-approval-edit-line"
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={300}
          disabled={busy}
        />
        <label className="mai-approval-label" htmlFor={`mai-reg-msg-${item.actionId}`}>
          {preview.drafted ? 'E-mail escrito pela IA (pode editar)' : 'E-mail (pode editar)'}
        </label>
        <textarea
          id={`mai-reg-msg-${item.actionId}`}
          className="mai-approval-edit"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          disabled={busy}
        />
        {(preview.warnings || []).length > 0 && (
          <ul className="mai-reg-confirm-warn">
            {preview.warnings.map((w) => <li key={w}>{w}</li>)}
          </ul>
        )}
        <div className="mai-btn-row">
          <button
            type="button"
            className="mai-btn"
            disabled={busy || !text.trim() || !subject.trim()}
            onClick={() => void run(() => confirmRegisterAction({ ...base, text, subject }), true)}
          >
            {busy ? 'Enviando…' : 'Enviar e-mail'}
          </button>
          <button
            type="button"
            className="mai-btn mai-btn-ghost"
            disabled={busy}
            onClick={() => void run(() => cancelRegisterAction(base))}
          >
            Cancelar
          </button>
        </div>
        {error && <p className="mai-form-error">{error}</p>}
      </div>
    );
  }

  // E-mail que não saiu: "Tentar de novo" só quando se sabe que nada saiu; com erro incerto, só o aviso (confira Enviados).
  if (outcome === 'failed' && preview?.kind === 'email') {
    const retryable = item.reasonCode === 'send_failed';
    return (
      <div className="mai-reg-confirm" role="group" aria-label={retryable ? 'Tentar de novo' : 'E-mail sem confirmação de envio'}>
        {preview.emailTo && (
          <p className="mai-reg-confirm-to">
            Para <strong>{preview.contactName}</strong> &lt;{preview.emailTo}&gt;
          </p>
        )}
        {item.result && <p className="mai-reg-confirm-to">{item.result}</p>}
        {retryable && (
          <div className="mai-btn-row">
            <button
              type="button"
              className="mai-btn"
              disabled={busy}
              onClick={() => void run(() => confirmRegisterAction(base), true)}
            >
              {busy ? 'Tentando…' : 'Tentar de novo'}
            </button>
          </div>
        )}
        {error && <p className="mai-form-error">{error}</p>}
      </div>
    );
  }

  if (outcome === 'awaiting_confirmation' && preview) {
    const isMessage = preview.kind === 'message';
    const scheduled = isMessage && !!preview.whenIso;
    return (
      <div className="mai-reg-confirm" role="group" aria-label="Confirmar envio">
        {isMessage ? (
          <>
            <p className="mai-reg-confirm-to">
              Para <strong>{preview.contactName}</strong>
              {preview.phoneTail ? ` (final ${preview.phoneTail})` : ''} · WhatsApp ·{' '}
              {scheduled ? `programada para ${preview.whenLabel || when(preview.whenIso)}` : 'agora'}
            </p>
            <label className="mai-approval-label" htmlFor={`mai-reg-msg-${item.actionId}`}>
              {preview.drafted ? 'Mensagem escrita pela IA (pode editar)' : 'Mensagem (pode editar)'}
            </label>
            <textarea
              id={`mai-reg-msg-${item.actionId}`}
              className="mai-approval-edit"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              disabled={busy}
            />
          </>
        ) : (
          <p className="mai-reg-confirm-to">{preview.summary}</p>
        )}
        {(preview.warnings || []).length > 0 && (
          <ul className="mai-reg-confirm-warn">
            {preview.warnings.map((w) => <li key={w}>{w}</li>)}
          </ul>
        )}
        <div className="mai-btn-row">
          <button
            type="button"
            className="mai-btn"
            disabled={busy || (isMessage && !text.trim())}
            onClick={() => void run(() => confirmRegisterAction({ ...base, text: isMessage ? text : undefined }))}
          >
            {busy ? 'Enviando…' : isMessage ? (scheduled ? 'Agendar' : 'Enviar') : 'Confirmar'}
          </button>
          <button
            type="button"
            className="mai-btn mai-btn-ghost"
            disabled={busy}
            onClick={() => void run(() => cancelRegisterAction(base))}
          >
            Cancelar
          </button>
        </div>
        {error && <p className="mai-form-error">{error}</p>}
      </div>
    );
  }

  // REG-37: a agenda falhou sem criar nada; o mesmo pedido pode ser refeito (sem duplicar) depois de reconectar.
  if (outcome === 'failed' && preview?.kind === 'retry') {
    return (
      <div className="mai-reg-confirm" role="group" aria-label="Tentar de novo">
        <p className="mai-reg-confirm-to">{preview.summary}</p>
        {(preview.warnings || []).length > 0 && (
          <ul className="mai-reg-confirm-warn">
            {preview.warnings.map((w) => <li key={w}>{w}</li>)}
          </ul>
        )}
        <div className="mai-btn-row">
          <button
            type="button"
            className="mai-btn"
            disabled={busy}
            onClick={() => void run(() => confirmRegisterAction(base))}
          >
            {busy ? 'Tentando…' : 'Tentar de novo'}
          </button>
        </div>
        {error && <p className="mai-form-error">{error}</p>}
      </div>
    );
  }

  // Esclarecimento amarrado a uma aprovação pendente é respondido na fila de Aprovações, não aqui.
  if ((outcome === 'needs_info' || outcome === 'existing_needs_info') && !item.approvalId) {
    const candidates = item.candidates || [];
    const answer = (
      <AnswerField
        item={item}
        busy={busy}
        onSend={(value) => run(() => answerRegisterAction({ ...base, answer: value }))}
      />
    );
    if (candidates.length === 0) {
      return (
        <div className="mai-reg-pick">
          {answer}
          {error && <p className="mai-form-error">{error}</p>}
        </div>
      );
    }
    return (
      <div className="mai-reg-pick" role="group" aria-label="Escolher o cliente">
        {candidates.map((c) => (
          <button
            key={c.id}
            type="button"
            className="mai-clarify-option"
            disabled={busy}
            onClick={() => void run(() => confirmRegisterAction({ ...base, contactId: c.id }))}
          >
            <CandidateLine c={c} />
          </button>
        ))}
        {answer}
        {error && <p className="mai-form-error">{error}</p>}
      </div>
    );
  }

  if (outcome === 'blocked' && preview?.blockedReason) {
    return <p className="mai-form-error" style={{ margin: '4px 0 0' }}>{preview.blockedReason}</p>;
  }

  if (outcome === 'executed' && item.canUndo) {
    return (
      <div className="mai-btn-row" style={{ marginTop: 4 }}>
        <button
          type="button"
          className="mai-linkish"
          disabled={busy}
          onClick={() => void run(() => undoRegisterAction(base))}
        >
          Desfazer
        </button>
        {error && <span className="mai-form-error">{error}</span>}
      </div>
    );
  }

  return null;
};
